import { useEffect, useRef } from "react";

import { ICustomer } from "redux/reducers/models/customer";

interface IUseReservationGuestSearchParams {
  guestQueries: string[];
  maxSlots: number;
  enabled?: boolean;
  searchCustomers: (query: string) => Promise<ICustomer[] | undefined>;
  onSlotResults: (slotIndex: number, results: ICustomer[]) => void;
  onSearching: (searching: boolean) => void;
}

export function useReservationGuestSearch({
  guestQueries,
  maxSlots,
  enabled = true,
  searchCustomers,
  onSlotResults,
  onSearching,
}: IUseReservationGuestSearchParams) {
  const searchCustomersRef = useRef(searchCustomers);
  const onSlotResultsRef = useRef(onSlotResults);
  const onSearchingRef = useRef(onSearching);

  searchCustomersRef.current = searchCustomers;
  onSlotResultsRef.current = onSlotResults;
  onSearchingRef.current = onSearching;

  const guestQueriesKey = guestQueries.slice(0, maxSlots).join("\u0000");

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const timeoutIds: Array<ReturnType<typeof setTimeout>> = [];
    let cancelled = false;

    for (let slotIndex = 0; slotIndex < maxSlots; slotIndex++) {
      const query = guestQueries[slotIndex] ?? "";
      const timeoutId = setTimeout(() => {
        void (async () => {
          if (cancelled) {
            return;
          }

          if (query === "") {
            onSlotResultsRef.current(slotIndex, []);
            return;
          }

          onSearchingRef.current(true);
          try {
            const guestResults = await searchCustomersRef.current(query);
            if (!cancelled) {
              onSlotResultsRef.current(slotIndex, guestResults ?? []);
            }
          } catch (error) {
            console.log("err", error);
          } finally {
            if (!cancelled) {
              onSearchingRef.current(false);
            }
          }
        })();
      }, 500);
      timeoutIds.push(timeoutId);
    }

    return () => {
      cancelled = true;
      timeoutIds.forEach(clearTimeout);
    };
  }, [enabled, guestQueries, guestQueriesKey, maxSlots]);
}
